/**
 * WRMS Context — Digi Sahyadri
 * Provides real-time notification state throughout the WRMS module.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationsForUser,
} from '../services/notificationService';
import { playNotificationSound } from '../../utils/soundService';
import {
  sendDesktopNotification,
  requestNotificationPermission,
} from '../../utils/desktopNotificationService';

const WRMSContext = createContext();

export const useWRMS = () => {
  const context = useContext(WRMSContext);
  if (!context) throw new Error('useWRMS must be used within WRMSProvider');
  return context;
};

// Helper to determine if a notification is genuinely new in this session
const isNotificationFresh = (notif, sessionStartTime) => {
  if (!notif) return false;

  let createdMs = null;
  if (notif.createdAt) {
    if (typeof notif.createdAt.toMillis === 'function') {
      createdMs = notif.createdAt.toMillis();
    } else if (typeof notif.createdAt.toDate === 'function') {
      createdMs = notif.createdAt.toDate().getTime();
    } else if (typeof notif.createdAt.seconds === 'number') {
      createdMs = notif.createdAt.seconds * 1000;
    } else if (typeof notif.createdAt === 'number') {
      createdMs = notif.createdAt;
    } else if (typeof notif.createdAt === 'string') {
      const parsed = Date.parse(notif.createdAt);
      if (!isNaN(parsed)) createdMs = parsed;
    }
  }

  // If createdAt exists and was created before the session started (with 5s buffer),
  // it is an existing/previous message. NEVER play sound or notify for it!
  if (createdMs !== null && createdMs < sessionStartTime - 5000) {
    return false;
  }

  return true;
};

export const WRMSProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const sessionStartTimeRef = useRef(Date.now());
  const knownNotificationIds = useRef(new Set());
  const isInitialLoad = useRef(true);

  // Real-time subscription to unread notifications with sound alert & desktop OS notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const userId = user.uid || user.id;
    const userRole = (user.role || 'employee').toLowerCase().trim();
    const userDept = user.department || '';
    const userIdsList = [
      user.uid,
      user.id,
      user.employeeId,
      user.employeeCode,
      user.job?.employeeId,
      user.job?.employeeCode,
    ].filter(Boolean);

    // Auto-request desktop notification permission on session start if pending
    requestNotificationPermission().catch(() => {});

    sessionStartTimeRef.current = Date.now();
    isInitialLoad.current = true;
    knownNotificationIds.current = new Set();

    const unsubscribe = subscribeToNotifications(userId, userRole, userDept, (notifs, isInitialSync) => {
      const currentUnread = notifs.filter(n => !n.read);

      if (isInitialLoad.current || isInitialSync) {
        // Initial sync / load:
        // Silently register all existing/previous notifications without playing sound or sending desktop alerts.
        notifs.forEach(n => knownNotificationIds.current.add(n.id));
        if (!isInitialSync) {
          isInitialLoad.current = false;
        }
      } else {
        // Real-time updates while actively using the system:
        // ONLY trigger audio chime & desktop alert for genuinely NEW incoming notifications
        const newIncoming = currentUnread.filter(n =>
          !knownNotificationIds.current.has(n.id) && isNotificationFresh(n, sessionStartTimeRef.current)
        );

        if (newIncoming.length > 0) {
          playNotificationSound();
          newIncoming.forEach((n) => {
            const reqType = n.requestType || 'leave';
            const targetUrl = n.requestId
              ? `/wrms/request/${reqType}/${n.requestId}`
              : '/wrms/approvals';
            sendDesktopNotification({
              title: n.title || 'Digi Sahyadri HRM Notification',
              body: n.message || 'You have a new request update',
              tag: `notif-${n.id}`,
              url: targetUrl,
              skipSound: true, // Sound already played once above for the incoming batch
            });
          });
        }

        // Keep known IDs registry up to date
        notifs.forEach(n => knownNotificationIds.current.add(n.id));
      }

      setNotifications(notifs);
      setUnreadCount(currentUnread.length);
    }, userIdsList);

    return () => unsubscribe();
  }, [user, isAuthenticated]);

  const fetchAllNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      const userRole = user.role || 'employee';
      const userDept = user.department || '';
      const userIdsList = [
        user.uid,
        user.id,
        user.employeeId,
        user.employeeCode,
        user.job?.employeeId,
        user.job?.employeeCode,
      ].filter(Boolean);
      const all = await getNotificationsForUser(userId, userRole, 50, userDept, userIdsList);
      all.forEach(n => knownNotificationIds.current.add(n.id));
      setNotifications(all);
      setUnreadCount(all.filter(n => !n.read).length);
    } catch (err) {
      console.error('[WRMSContext] fetchAllNotifications error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markRead = async (notifId, recipientType = 'user') => {
    const userId = user?.uid || user?.id;
    await markNotificationRead(notifId, userId, recipientType);
    setNotifications(prev => prev.map(n =>
      n.id === notifId ? { ...n, read: true } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read && n.recipientType !== 'role').map(n => n.id);
    if (unreadIds.length > 0) {
      await markAllNotificationsRead(user?.uid || user?.id, unreadIds);
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <WRMSContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      markRead,
      markAllRead,
      fetchAllNotifications,
    }}>
      {children}
    </WRMSContext.Provider>
  );
};

export default WRMSContext;
